/**
 * Farmers open at the gate, live, from the phones' backups (GET /gate/open-deliveries). Only a
 * view: a purchase becomes a lot and a payout when the phone completes it, as before.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { toQuery } from './core'
import { ck } from './keys'
import { arr, iso, num, numOrNull, obj, str, strOrNull } from './mappers'

/** How often the gate's live boards refresh while the page is in front. */
export const LIVE_REFRESH_MS = 30_000

export type OpenDeliveryStatus = 'OPEN' | 'COMPLETED' | 'CANCELLED'

export interface OpenDeliveryRow {
  deliveryUlid: string
  status: OpenDeliveryStatus
  farmerId: string | null
  farmerName: string | null
  farmerCode: string | null
  cropName: string | null
  captureMode: string
  step: string | null
  bags: number
  grossG: number
  mainLotCode: string | null
  startedAt: string | null
  phoneUpdatedAt: string | null
  receivedAt: string | null
  groupId: string | null
  villageName: string | null
  devicePrefix: string | null
  actorName: string | null
}

export interface OpenDeliveryFilters {
  groupId?: string
  status?: OpenDeliveryStatus | 'ALL'
  days?: number
}

function mapRow(raw: unknown): OpenDeliveryRow {
  const r = obj(raw)
  const status = str(r.status)
  return {
    deliveryUlid: str(r.deliveryUlid),
    status: status === 'COMPLETED' || status === 'CANCELLED' ? status : 'OPEN',
    farmerId: strOrNull(r.farmerId),
    farmerName: strOrNull(r.farmerName),
    farmerCode: strOrNull(r.farmerCode),
    cropName: strOrNull(r.cropName),
    captureMode: str(r.captureMode) || 'APP',
    step: strOrNull(r.step),
    bags: num(r.bags),
    grossG: numOrNull(r.grossG) ?? 0,
    mainLotCode: strOrNull(r.mainLotCode),
    startedAt: iso(r.startedAt),
    phoneUpdatedAt: iso(r.phoneUpdatedAt),
    receivedAt: iso(r.receivedAt),
    groupId: strOrNull(r.groupId),
    villageName: strOrNull(r.villageName),
    devicePrefix: strOrNull(r.devicePrefix),
    actorName: strOrNull(r.actorName),
  }
}

export function useOpenDeliveries(f: OpenDeliveryFilters) {
  return useQuery({
    queryKey: [...ck.sessions, 'open-deliveries', f],
    queryFn: async () => {
      const body = obj(await api.get('/gate/open-deliveries', { query: toQuery(f) }))
      return arr(body.rows).map(mapRow)
    },
    refetchInterval: LIVE_REFRESH_MS,
  })
}

export interface OpenDeliveryBag {
  id: string
  tag: string | null
  red: boolean
  withdrawn: boolean
  grossG: number | null
  weighings: number
}

export interface OpenDeliveryDetail {
  deliveryUlid: string
  status: OpenDeliveryStatus
  bags: OpenDeliveryBag[]
  moisturePct: number | null
  phoneUpdatedAt: string | null
}

/** One open farmer's bags, read from the phone's backup. */
export function useOpenDelivery(deliveryUlid: string | null) {
  return useQuery({
    queryKey: [...ck.sessions, 'open-delivery', deliveryUlid],
    enabled: Boolean(deliveryUlid),
    refetchInterval: LIVE_REFRESH_MS,
    queryFn: async (): Promise<OpenDeliveryDetail> => {
      const r = obj(await api.get(`/gate/open-deliveries/${deliveryUlid}`))
      const data = obj(r.data)
      const bags = arr(data.bags).map((raw) => {
        const b = obj(raw)
        const weights = arr(b.weights).map(obj)
        const superseded = new Set(weights.map((w) => w.supersedes_id))
        const head = weights.filter((w) => !superseded.has(w.id)).pop()
        return {
          id: str(b.id),
          tag: strOrNull(b.qr_payload),
          red: b.red === true,
          withdrawn: b.withdrawn === true,
          grossG: head ? numOrNull(head.gross_g) : null,
          weighings: weights.length,
        }
      })
      const readings = arr(obj(data.moisture).readings).map(obj)
      const chosen =
        readings.find((x) => x.chosen_by_farmer === true) ?? readings[readings.length - 1]
      const tenths = chosen ? numOrNull(chosen.tenths) : null
      const status = str(r.status)
      return {
        deliveryUlid: str(r.deliveryUlid),
        status: status === 'COMPLETED' || status === 'CANCELLED' ? status : 'OPEN',
        bags,
        moisturePct: tenths === null ? null : tenths / 10,
        phoneUpdatedAt: iso(r.phoneUpdatedAt),
      }
    },
  })
}
